// The review family's REVIEW OUTBOX kinds (plan 2026-11-03/102 slice 03): the paced drain
// that posts a frozen review to its PR part by part. Split out of `review.ts` for size, like
// `./review-close-loop.ts`, and spread into the same composed table; `review.test.ts` proves
// the groups disjoint. Payload contract: `../../audit/payload-review-outbox.ts`. Every row is
// `visibleToMaintainer: true`, but each render still reads the payload defensively
// (`./review-payload` accessors): a missing field renders the token `unknown`, never a
// fabricated count, and the subject falls back to the repo/session line.

import {
  attemptParams,
  type TerminalFailRenderContext,
  type TerminalFailRendered,
  type TerminalRenderContext,
  type TerminalRendered,
  terminalRendererGroup,
} from '../../../wire/terminal';
import { numOpt, prSubject, rec, refsFor, strOpt, strOr } from './review-payload';

/** A count as a line param: the number, or `unknown` — never a made-up zero. */
function count(v: unknown): string {
  const n = numOpt(v);
  return n === null ? 'unknown' : String(n);
}

/** A boolean as a line param token, or `unknown`. */
function flag(v: unknown, yes: string, no: string): string {
  return v === true ? yes : v === false ? no : 'unknown';
}

/** The PR subject and refs every outbox row shares, read off the shared input. */
function outboxSubject(ctx: TerminalRenderContext) {
  const input = rec(ctx.row.input);
  const repo = strOpt(input['repo']) ?? ctx.row.repoFullName;
  const pr = numOpt(input['prNumber']);
  const { sessionId } = ctx.row;
  return {
    subject: prSubject(repo, pr, sessionId),
    refs: refsFor({ repo, pr, taskId: strOpt(input['taskId']), sessionId }),
  };
}

const line =
  (
    key: `terminal.review.outbox_${string}`,
    params: (output: Record<string, unknown>) => Record<string, string>,
  ) =>
  (ctx: TerminalRenderContext): TerminalRendered => ({
    line: { key, params: params(rec(ctx.row.output)) },
    ...outboxSubject(ctx),
  });

// ─── review.outbox_dead (fail) ───────────────────────────────────────────────
// The drain's dead letter. `attempts` is the payload's own count; `attempt`/`of` are the
// shared ladder params every fail line carries (`attemptParams`), which this row's `output`
// does not feed (it names `attempts`, not `attempt`), so they read `unknown`. `lastError` is
// left off the line: it is an operator's string, read from the row's raw payload.
function outboxDead(ctx: TerminalFailRenderContext): TerminalFailRendered {
  const output = rec(ctx.row.output);
  return {
    line: {
      key: 'terminal.review.outbox_dead',
      params: {
        ...attemptParams(ctx.attempt),
        attempts: count(output['attempts']),
        posted: count(output['posted']),
        total: count(output['total']),
      },
    },
    ...outboxSubject(ctx),
  };
}

/** This sub-table's entries, spread into `review.ts`'s composed export. */
export const TERMINAL_RENDERERS_REVIEW_OUTBOX = terminalRendererGroup({
  'review.outbox_enqueued': {
    level: 'decision',
    actor: 'reviewer',
    render: line('terminal.review.outbox_enqueued', (o) => ({
      total: count(o['total']),
      event: strOr(o['event'], 'unknown'),
    })),
  },
  'review.outbox_part_posted': {
    level: 'decision',
    actor: 'reviewer',
    render: line('terminal.review.outbox_part_posted', (o) => ({
      part: count(o['part']),
      posted: count(o['posted']),
      total: count(o['total']),
      final: flag(o['final'], 'final', 'partial'),
    })),
  },
  'review.outbox_reshaped': {
    level: 'decision',
    actor: 'reviewer',
    render: line('terminal.review.outbox_reshaped', (o) => ({
      from: count(o['from']),
      to: count(o['to']),
    })),
  },
  'review.outbox_comment_retired': {
    level: 'decision',
    actor: 'reviewer',
    render: line('terminal.review.outbox_comment_retired', (o) => ({
      count: Array.isArray(o['ordinals']) ? String(o['ordinals'].length) : 'unknown',
      reason: strOr(o['reason'], 'unknown'),
    })),
  },
  'review.outbox_delivered': {
    level: 'decision',
    actor: 'reviewer',
    render: line('terminal.review.outbox_delivered', (o) => ({
      event: strOr(o['reviewEvent'], 'unknown'),
      posted: count(o['posted']),
      total: count(o['total']),
      how: flag(o['reconciled'], 'found on the PR', 'posted'),
    })),
  },
  'review.outbox_retired': {
    level: 'warn',
    actor: 'reviewer',
    render: line('terminal.review.outbox_retired', (o) => ({
      reason: strOr(o['reason'], 'unknown'),
      posted: count(o['posted']),
      total: count(o['total']),
    })),
  },
  'review.outbox_dead': { level: 'fail', actor: 'reviewer', render: outboxDead },
  // Plan 102 slice 04: a silent run whose box had sealed its review into the outbox is
  // settled `completed`, not cancelled or requeued. No count to read: the line is the fact.
  'review.handed_off': {
    level: 'decision',
    actor: 'reviewer',
    render: (ctx: TerminalRenderContext): TerminalRendered => ({
      line: { key: 'terminal.review.handed_off', params: {} },
      ...outboxSubject(ctx),
    }),
  },
});
